import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Globe, Plus, Trash2, Save, X, RefreshCw, Eye, EyeOff, ArrowUp, ArrowDown } from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = 'w-full bg-white dark:bg-[#0f0f10] border border-neutral-200 dark:border-neutral-800 rounded-md px-3 py-2 text-[13.5px] outline-none focus:border-neutral-400';
const slugify = (s) => (s || '').toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').slice(0, 40);
const empty = { id: '', name: '', code: '', flag: '', is_active: true };

const CountriesPanel = () => {
  const { password } = useAdminAuth();
  const headers = useMemo(() => ({ 'X-Admin-Password': password }), [password]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setMsg('');
    try { setRows((await axios.get(`${API}/admin/countries`, { headers })).data); }
    catch (e) { setMsg('Failed to load: ' + (e?.response?.data?.detail || e.message)); }
    finally { setLoading(false); }
  }, [headers]);
  useEffect(() => { load(); }, [load]);

  const upd = (k, v) => setEditing(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      const payload = {
        name: editing.name.trim(),
        code: (editing.code || '').trim().toUpperCase(),
        flag: (editing.flag || '').trim(),
        is_active: !!editing.is_active,
      };
      if (editing.isNew) {
        payload.id = editing.id.trim().toLowerCase() || slugify(editing.name);
        if (!payload.id) throw new Error('ID required');
        await axios.post(`${API}/admin/countries`, payload, { headers });
      } else {
        await axios.patch(`${API}/admin/countries/${editing._origId}`, payload, { headers });
      }
      setEditing(null); await load();
    } catch (e) { setMsg('Save failed: ' + (e?.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.name}"? Customers won't be able to select this country.`)) return;
    try { await axios.delete(`${API}/admin/countries/${r.id}`, { headers }); await load(); }
    catch { setMsg('Delete failed'); }
  };

  const toggleActive = async (r) => {
    try { await axios.patch(`${API}/admin/countries/${r.id}`, { is_active: !r.is_active }, { headers }); await load(); }
    catch { setMsg('Toggle failed'); }
  };

  const move = async (r, dir) => {
    const i = rows.findIndex(x => x.id === r.id);
    const other = rows[dir === 'up' ? i - 1 : i + 1];
    if (!other) return;
    try {
      await Promise.all([
        axios.patch(`${API}/admin/countries/${r.id}`, { sort_order: other.sort_order }, { headers }),
        axios.patch(`${API}/admin/countries/${other.id}`, { sort_order: r.sort_order }, { headers }),
      ]);
      await load();
    } catch { setMsg('Reorder failed'); }
  };

  return (
    <div className="space-y-4" data-testid="countries-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-[24px] text-neutral-900 dark:text-neutral-100 inline-flex items-center gap-2"><Globe size={20} className="text-neutral-400" /> Shipping countries</h2>
          <p className="text-[12.5px] text-neutral-500">Customers can only ship to countries marked <span className="text-emerald-600 font-medium">Live</span>. The Country dropdown at checkout is built from this list.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="inline-flex items-center gap-1.5 border border-neutral-200 dark:border-neutral-700 rounded-md px-3 py-2 text-[13px]"><RefreshCw size={13} /> Refresh</button>
          <button data-testid="add-country-btn" onClick={() => setEditing({ ...empty, isNew: true })} className="btn-brand inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-[13.5px] font-semibold"><Plus size={14} /> Add country</button>
        </div>
      </div>
      {msg && <div className="text-red-600 text-[13px]" data-testid="countries-error">{msg}</div>}

      <div className="rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#141416] overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-4 py-2.5 text-[10.5px] uppercase tracking-widest text-neutral-500 border-b border-neutral-200 dark:border-neutral-800">
          <div className="col-span-1">Order</div>
          <div className="col-span-1">Flag</div>
          <div className="col-span-5">Name</div>
          <div className="col-span-2">Code</div>
          <div className="col-span-1 text-center">Live</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {loading ? (
          <div className="p-6 text-center text-neutral-500 text-[13.5px]">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center text-neutral-500 text-[13.5px]">No countries yet. Add one to open the storefront to buyers.</div>
        ) : (
          rows.map((r) => (
            <div key={r.id} data-testid={`country-row-${r.id}`} className="grid grid-cols-12 gap-3 px-4 py-3 items-center border-b border-neutral-100 dark:border-neutral-800 last:border-b-0">
              <div className="col-span-1 flex items-center gap-1">
                <span className="text-[12px] text-neutral-500 w-4">{r.sort_order}</span>
                <button onClick={() => move(r, 'up')} className="p-1 rounded hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-500"><ArrowUp size={12} /></button>
                <button onClick={() => move(r, 'down')} className="p-1 rounded hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-500"><ArrowDown size={12} /></button>
              </div>
              <div className="col-span-1 text-[22px] leading-none">{r.flag || '🌐'}</div>
              <div className="col-span-5">
                <div className="text-[13.5px] text-neutral-900 dark:text-neutral-100 truncate">{r.name}</div>
                <div className="text-[11.5px] text-neutral-500 truncate">{r.id}</div>
              </div>
              <div className="col-span-2 font-mono text-[12.5px] text-neutral-600 dark:text-neutral-300">{r.code || '—'}</div>
              <div className="col-span-1 flex justify-center">
                <button data-testid={`country-toggle-${r.id}`} onClick={() => toggleActive(r)} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium ${r.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-neutral-200 text-neutral-600'}`}>
                  {r.is_active ? <Eye size={11} /> : <EyeOff size={11} />} {r.is_active ? 'Live' : 'Hidden'}
                </button>
              </div>
              <div className="col-span-2 flex items-center justify-end gap-1">
                <button data-testid={`country-edit-${r.id}`} onClick={() => setEditing({ ...r, isNew: false, _origId: r.id })} className="px-2 py-1 text-[12px] rounded-md border border-neutral-200 dark:border-neutral-700">Edit</button>
                <button data-testid={`country-delete-${r.id}`} onClick={() => remove(r)} className="p-1.5 rounded-md text-neutral-500 hover:text-red-500 hover:bg-neutral-100 dark:hover:bg-neutral-800"><Trash2 size={13} /></button>
              </div>
            </div>
          ))
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 backdrop-blur-sm p-4 overflow-y-auto" onClick={() => setEditing(null)}>
          <div data-testid="country-form-modal" className="mt-16 w-full max-w-lg bg-white dark:bg-[#141416] rounded-xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200 dark:border-neutral-800">
              <h3 className="font-serif text-[18px] text-neutral-900 dark:text-neutral-100">{editing.isNew ? 'Add country' : 'Edit country'}</h3>
              <button onClick={() => setEditing(null)} className="p-1.5 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-3">
              <label className="block">
                <span className="text-[11px] tracking-widest uppercase text-neutral-500">Name</span>
                <input data-testid="country-name-input" className={inputCls} value={editing.name} onChange={(e) => upd('name', e.target.value)} placeholder="e.g. Canada" />
              </label>
              <label className="block">
                <span className="text-[11px] tracking-widest uppercase text-neutral-500">ID (slug){editing.isNew && <span className="text-neutral-400 normal-case tracking-normal"> — auto from name if left blank</span>}</span>
                <input data-testid="country-id-input" className={inputCls + (editing.isNew ? '' : ' opacity-60 cursor-not-allowed')} disabled={!editing.isNew} value={editing.id} onChange={(e) => upd('id', slugify(e.target.value))} placeholder={editing.isNew ? slugify(editing.name) || 'e.g. canada' : ''} />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-[11px] tracking-widest uppercase text-neutral-500">ISO code</span>
                  <input data-testid="country-code-input" maxLength={3} className={inputCls} value={editing.code} onChange={(e) => upd('code', e.target.value.toUpperCase())} placeholder="e.g. CA" />
                </label>
                <label className="block">
                  <span className="text-[11px] tracking-widest uppercase text-neutral-500">Flag emoji</span>
                  <input data-testid="country-flag-input" className={inputCls} value={editing.flag} onChange={(e) => upd('flag', e.target.value)} placeholder="🇨🇦" />
                </label>
              </div>
              <label className="inline-flex items-center gap-2 text-[13px]">
                <input type="checkbox" checked={!!editing.is_active} onChange={(e) => upd('is_active', e.target.checked)} className="w-4 h-4 accent-emerald-600" />
                Active (show at checkout)
              </label>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-neutral-200 dark:border-neutral-800">
              <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-md border border-neutral-200 dark:border-neutral-700 text-[13.5px]">Cancel</button>
              <button data-testid="country-save-btn" onClick={save} disabled={saving || !editing.name.trim()} className="btn-brand inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-[13.5px] font-semibold disabled:opacity-60"><Save size={14} /> {saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CountriesPanel;
